import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuthUser } from "../../contexts/AuthUserContext";
import React, { useState, useEffect, useRef } from "react";
import { useAuth0 } from "../../components/auth/DevAuth0Provider";
import { fetchTickets } from "../../api/ticketData";
import TicketCard from "../tickets/ticketCard";
import NewTicket from "../tickets/NewTicket";
import { useTheme } from "../../contexts/ThemeContext";

import JSONPretty from "react-json-pretty";
import 'react-json-pretty/themes/adventure_time.css';
import 'react-json-pretty/themes/acai.css';


import Markdown from "react-markdown";
import { getCallDurationInSeconds } from "../../api/callData";

const SectionTitle = ({ text }) => (
    <h3 className="subheader-textbox text-sm">{text}</h3>
);

// Helper component to render each key-value pair
const DetailItem = ({ label, value }) => (
    <div className="text-sm">
        <span className="font-medium capitalize text-secondary">{label.replace(/_/g, ' ')}: </span>
        <span className="text-font-secondary">{value?.toString() || 'Not specified'}</span>
    </div>
);

export const BookingTab = ({ call }) => {
    // Handle both cart_data and legacy booking_info structures
    const bookingData = call.cart_data || call.booking_info;
    const isCartData = !!call.cart_data;
    
    // Check if there's booking/cart data
    const hasBooking = isCartData 
        ? bookingData?.full_cart?.items?.length > 0
        : bookingData?.equipment_requested?.length > 0;

    // A helper to render object data into a card
    const renderInfoCard = (data) => (
        <div className="space-y-2 text-sm">
            {Object.entries(data).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                    <span className="text-font-secondary capitalize">{key.replace(/_/g, ' ')}:</span>
                    <span className="text-font-primary">
                        {value === null || value === undefined || value === ''
                            ? 'Not specified'
                            : typeof value === 'object'
                                ? JSON.stringify(value)
                                : typeof value === 'boolean'
                                    ? (value ? 'Yes' : 'No')
                                    : value.toString()
                        }
                    </span>
                </div>
            ))}
        </div>
    );

    // Helper to render cart items
    const renderCartItems = (items) => (
        <div className="space-y-4">
            {items.map((item, index) => (
                <div key={index} className="border-2 border-primary-50 rounded-md p-3 flex flex-col gap-3">
                    <div className="flex items-center gap-1">
                        <Icon icon="material-symbols:construction" className="responsive-icon-sm text-primary" />
                        <span className="font-semibold text-font-primary text-sm sm:text-base">
                            {item.name} {item.model && `(${item.model})`}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex gap-2">
                            <span className="text-font-secondary">Quantity:</span>
                            <span className="text-font-primary">{item.quantity || 'N/A'}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-font-secondary">Type:</span>
                            <span className="text-font-primary capitalize">{item.type?.replace(/_/g, ' ') || 'N/A'}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-font-secondary">Load Type:</span>
                            <span className="text-font-primary">{item.load_type || 'N/A'}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-font-secondary">Rental Days:</span>
                            <span className="text-font-primary">{item.rental_days || 'N/A'}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-font-secondary">Rental Hours:</span>
                            <span className="text-font-primary">{item.rental_hours || 'N/A'}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-font-secondary">Start Date:</span>
                            <span className="text-font-primary">{item.rental_start_date || 'N/A'}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-font-secondary">Start Time:</span>
                            <span className="text-font-primary">{item.rental_start_time || 'N/A'}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-font-secondary">Delivery/Pickup:</span>
                            <span className="text-font-primary capitalize">{item.delivery_vs_pickup || 'N/A'}</span>
                        </div>
                        {item.attachments && item.attachments.length > 0 && (
                            <div className="col-span-2 text-sm">
                                <span className="text-font-secondary">Attachments: </span>
                                <span className="text-font-primary">{item.attachments.join(', ')}</span>
                            </div>
                        )}
                    </div>
                    <hr className="border-border border-b-2" />
                    <div className="flex justify-between text-sm">
                        <span className="text-font-secondary">Rental Cost:</span>
                        <span className="font-semibold text-primary">${item.rental_cost || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-font-secondary">Delivery Cost:</span>
                        <span className="font-semibold text-primary">${item.delivery_cost || 0}</span>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="w-full flex-1 flex flex-col gap-2 overflow-y-auto scrollable">
            {hasBooking ? (
                <div className="w-full flex flex-col gap-3">
                    <SectionTitle text={isCartData ? "Cart Items" : "Equipment Requested"} />
                    {isCartData ? (
                        renderCartItems(bookingData.full_cart.items)
                    ) : (
                        <div className="space-y-2">
                            {bookingData.equipment_requested.map((equipment, index) => (
                                <div key={index} className="bg-bgSecondary button-container rounded-lg p-3 flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                        <Icon icon="material-symbols:construction" className="text-primary" />
                                        <span className="font-semibold text-primary">
                                            {equipment.type} {equipment.model && `(${equipment.model})`}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div className="flex gap-2">
                                            <span className="text-font-secondary">Quantity:</span>
                                            <span className="text-font-primary">{equipment.quantity || 'N/A'}</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <span className="text-font-secondary">Duration:</span>
                                            <span className="text-font-primary">{equipment.duration || 'N/A'}</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <span className="text-font-secondary">Start Date:</span>
                                            <span className="text-font-primary">{equipment.start_date || 'N/A'}</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <span className="text-font-secondary">End Date:</span>
                                            <span className="text-font-primary">{equipment.end_date || 'N/A'}</span>
                                        </div>
                                        {equipment.special_requirements && (
                                            <div className="text-sm">
                                                <span className="text-font-secondary">Special Requirements: </span>
                                                <span className="text-font-primary">{equipment.special_requirements}</span>
                                            </div>
                                        )}
                                    </div>
                                    <hr className="border-bgAccent" />
                                    <div className="flex justify-between text-sm">
                                        <span className="text-font-secondary">Pricing:</span>
                                        {equipment.pricing_discussed && equipment.pricing_discussed.trim().length > 0 ? (
                                            <span className="font-semibold text-primary">{equipment.pricing_discussed}</span>
                                        ) : (
                                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                                <span className="text-sm">Not Available</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <hr className="border-b-2 border-bgAccent" />

                    <div className="flex flex-col gap-2">
                        <SectionTitle text="Customer Information" />
                        {isCartData ? (
                            // Cart data customer structure
                            bookingData?.full_cart?.customer_info && Object.keys(bookingData.full_cart.customer_info).length > 0 ? (
                                renderInfoCard(bookingData.full_cart.customer_info)
                            ) : (
                                <div className="flex flex-row gap-1 items-center text-font-secondary">
                                    <Icon icon="material-symbols:info-outline" className="text-lg" />
                                    <span className="text-sm">Customer information not provided</span>
                                </div>
                            )
                        ) : (
                            // Legacy booking_info customer structure
                            bookingData?.customer_info && Object.keys(bookingData.customer_info).length > 0 ? (
                                renderInfoCard(bookingData.customer_info)
                            ) : (
                                <div className="flex flex-row gap-1 items-center text-font-secondary">
                                    <Icon icon="material-symbols:info-outline" className="text-lg" />
                                    <span className="text-sm">Not Available</span>
                                </div>
                            )
                        )}
                    </div>

                    <hr className="border-b-2 border-bgAccent" />

                    <div className="flex flex-col gap-2">
                        <SectionTitle text="Delivery Information" />
                        {isCartData ? (
                            // Cart data delivery structure
                            bookingData?.full_cart?.delivery && Object.keys(bookingData.full_cart.delivery).length > 0 ? (
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-font-secondary">Method:</span>
                                        <span className="text-font-primary">{bookingData.full_cart.delivery.method || 'Not specified'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-font-secondary">Total Cost:</span>
                                        <span className="text-font-primary font-semibold">${bookingData.full_cart.delivery.total_cost || 0}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-font-secondary">Bundling Applied:</span>
                                        <span className={`p-1.5 rounded text-xs text-font-primary justify-center ${bookingData.full_cart.delivery.bundling_applied ? 'tag-green' : 'tag-red'}`}>
                                            {bookingData.full_cart.delivery.bundling_applied ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                    {bookingData.full_cart.delivery.bundling_details && bookingData.full_cart.delivery.bundling_details.length > 0 && (
                                        <div className="flex flex-col items-start gap-1">
                                            <span className="text-font-secondary">Bundling Details:</span>
                                            <div className="text-font-primary">
                                                {bookingData.full_cart.delivery.bundling_details.map((detail, index) => (
                                                    <div key={index}>• {detail}</div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="flex flex-row gap-1 items-center text-font-secondary">
                                    <Icon icon="material-symbols:info-outline" className="text-lg" />
                                    <span className="text-sm">Delivery information not available</span>
                                </div>
                            )
                        ) : (
                            // Legacy booking_info delivery structure
                            bookingData?.delivery_info && Object.keys(bookingData.delivery_info).length > 0 ? (
                                renderInfoCard(bookingData.delivery_info)
                            ) : (
                                <div className="flex flex-row gap-1 items-center text-font-secondary">
                                    <Icon icon="material-symbols:info-outline" className="text-lg" />
                                    <span className="text-sm">Not Available</span>
                                </div>
                            )
                        )}
                    </div>
                    
                    <hr className="border-b-2 border-bgAccent" />

                    {/* Cart Summary - Only show for cart_data */}
                    {isCartData && (
                        <div className="flex flex-col gap-2">
                            <SectionTitle text="Cart Summary" />
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Customer Name:</span>
                                    <span className="text-font-primary">{bookingData.summary.customer_name || 'Not provided'}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-font-secondary">Customer Info Status:</span>
                                    <span className={`p-1.5 rounded text-xs text-font-primary justify-center ${bookingData.summary.has_customer_info ? 'tag-green' : 'tag-orange'}`}>
                                        {bookingData.summary.has_customer_info ? 'Complete' : 'Incomplete'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Last Updated:</span>
                                    <span className="text-font-primary">{new Date(bookingData.summary.updated_at).toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Pricing Information - Only show for legacy booking_info */}
                    {!isCartData && (
                        <div className="flex flex-col gap-2">
                            <SectionTitle text="Pricing Information" />
                            {bookingData?.pricing_info && Object.keys(bookingData.pricing_info).length > 0 ? (
                                renderInfoCard(bookingData.pricing_info)
                            ) : (
                                <div className="flex flex-row gap-1 items-center text-font-secondary">
                                    <Icon icon="material-symbols:info-outline" className="text-lg" />
                                    <span className="text-sm">Not Available</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Booking Status Details - Only show for legacy booking_info */}
                    {!isCartData && (
                        <div className="flex flex-col gap-2">
                            <SectionTitle text="Next Steps" />
                            {bookingData?.booking_status?.next_steps ? (
                                <p className="text-sm text-font-primary">{bookingData.booking_status.next_steps}</p>
                            ) : (
                                <div className="flex flex-row gap-1 items-center text-font-secondary">
                                    <Icon icon="material-symbols:info-outline" className="text-lg" />
                                    <span className="text-sm">Not Available</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Additional Notes - Only show for legacy booking_info */}
                    {!isCartData && (
                        <div className="flex flex-col gap-2">
                            <SectionTitle text="Additional Notes" />
                            {bookingData?.additional_notes && bookingData.additional_notes.trim().length > 0 ? (
                                <p className="text-sm text-font-primary">{bookingData.additional_notes}</p>
                            ) : (
                                <div className="flex flex-row gap-1 items-center text-font-secondary">
                                    <Icon icon="material-symbols:info-outline" className="text-lg" />
                                    <span className="text-sm">Not Available</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-10 w-full text-center text-gray-400">
                    <Icon icon="mdi:book-off-outline" className="text-5xl mb-2" />
                    <p>No Booking Made</p>
                </div>
            )}
        </div>
    );
};

export const TranscriptsTab = ({ call }) => {
    // Function to remove SSML tags from content
    const cleanSSMLContent = (content) => {
        if (!content || typeof content !== 'string') return content;
        
        // Remove SSML tags while preserving the text content
        return content
            .replace(/<[^>]*>/g, '') // Remove all XML/SSML tags
            .replace(/\s+/g, ' ')    // Normalize whitespace
            .trim();                 // Remove leading/trailing whitespace
    };

    return(
        <div className="w-full h-full p-2 items-start flex flex-col gap-6">
            {call.conversation.map((message, index) => {
                return(
                    <div key={index} className={`flex w-full font-medium gap-2 items-start ${message.role === 'assistant' ? 'flex-row' : 'flex-row-reverse'}`}>
                        <div className={`p-3 rounded-md ${message.role === 'assistant' ? 'tag-primary' : 'tag-gray'}`}>
                            <Icon icon={message.role === 'assistant' ? 'mingcute:ai-fill' : 'mdi:user'} className={`text-xl ${message.role === 'assistant' ? 'text-primary' : 'text-accent'}`} />
                        </div>
                        <div className={`flex flex-col w-fit gap-2 w-4/5 ${message.role === 'assistant' ? 'flex-row items-end' : 'flex-row-reverse font-primary'}`}>
                            <div className={`p-3 text-sm flex-1 rounded-md ${message.role === 'assistant' ? 'tag-primary' : 'tag-gray'}`}>
                                {cleanSSMLContent(message.content)}
                            </div>
                            <p className="text-xs text-font-secondary">{message.timestamp ? message.timestamp.slice(11, 19) : 'N/A'}</p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export const AnalyticsTab = ({ call, isSummary, user }) => {
    const { theme } = useTheme();

    // Function to remove SSML tags from content
    const cleanSSMLContent = (content) => {
        if (!content || typeof content !== 'string') return content;
        
        // Remove SSML tags while preserving the text content
        return content
            .replace(/<[^>]*>/g, '') // Remove all XML/SSML tags
            .replace(/\s+/g, ' ')    // Normalize whitespace
            .trim();                 // Remove leading/trailing whitespace
    };

    const truncatedSummary = call.summary ? cleanSSMLContent(call.summary.split(/\s*(?:###\s*)?ORDER DETAILS/)[0].trim()) : 'No summary available.';

    return(
        <div className="px-2 w-full h-full flex items-start flex flex-col">
            {isSummary ? (
                <div className="flex flex-col gap-3 w-full">
                    {user.role === 'helios_admin' && (
                        <div className="flex flex-row justify-between items-center">
                            <h3 className="subheader-textbox text-sm">Call Summary</h3>
                            <div className={
                                `py-2 rounded-lg flex items-center gap-2
                                ${call.success_score >= 7 ? 'text-green-500' : call.success_score >= 4 ? 'text-yellow-500' : 'text-error'}
                            `}>
                                <Icon icon="line-md:check-all" className="text-xl" />
                                Score: {call.success_score || 'N/A'} ({call.score_status || 'N/A'})
                            </div>
                        </div>
                    )}
                    <div className='card-container bg-background rounded-md font-medium p-3 gap-2 flex flex-col'>
                        <Markdown>{truncatedSummary}</Markdown>
                    </div>
                    <div className='card-container bg-background rounded-md font-medium p-3 gap-2 flex flex-col'>
                        <Markdown>{(typeof call.evaluation === 'string' ? call.evaluation : `No evaluation available.`)}</Markdown>
                    </div>
                    
                    {user.role === 'helios_admin' && call.analytics && (
                        <div className="mt-2">
                            <h3 className="subheader-textbox text-sm mb-2">Analytics</h3>
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-x-2 gap-y-3">
                                {(() => {
                                    const durationSec = getCallDurationInSeconds(call);
                                    return Object.entries(call.analytics)
                                    .sort(([keyA], [keyB]) => {
                                        const order = {
                                            'message_count': 1,
                                            'user_messages': 2,
                                            'assistant_messages': 3,
                                            'average_response_time': 4,
                                            'duration': 5,
                                            'sentiment_analysis': 6,
                                            'tokens': 7,
                                            'interaction_metrics': 8
                                        };
                                        return (order[keyA] || 999) - (order[keyB] || 999);
                                    })
                                    .map(([key, value], index) => {
                                        // Calculate average_response_time if it's null
                                        if (key === 'average_response_time' && value === null && call.analytics.message_count && durationSec > 0) {
                                            value = `${(durationSec / call.analytics.message_count).toFixed(2)}s`;
                                        }
                                        // Append 's' to duration for units (use resolved duration for display)
                                        else if (key === 'duration') {
                                            value = `${durationSec}s`;
                                        }

                                        return (
                                            <div 
                                                key={key} 
                                                className={`card-container bg-background p-3 rounded-md capitalize 
                                                    ${index < 3 ? 'col-span-2' : 'col-span-3'}
                                                    ${index < 5 ? 'flex flex-row items-center justify-between' : ''}
                                                    `}
                                            >
                                                <p className="text-sm font-medium">{key.replace(/_/g, ' ')}</p>
                                                <div className="text-base text-primary font-semibold">
                                                    {typeof value === 'object' ? 
                                                        <JSONPretty data={value} theme={theme === 'dark' ? 'adventure_time' : 'acai'} mainStyle="background-color:transparent;" /> 
                                                        : value.toString()
                                                    }
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <JSONPretty data={call} theme={theme === 'dark' ? 'adventure_time' : 'acai'} mainStyle="background-color:transparent;" />
            )}
        </div>
    );
}

export const TicketsTab = ({ call, isLogs }) => {
    const user = useAuthUser();

    // State for displaying existing ticket logs
    const [tickets, setTickets] = useState([]);
    const [isLoadingTickets, setIsLoadingTickets] = useState(false);
    const [fetchTicketsError, setFetchTicketsError] = useState(null);

    const callId = call?.id;

    useEffect(() => {
        if (isLogs && callId) {
            const loadTickets = async () => {
                setIsLoadingTickets(true);
                setFetchTicketsError(null);
                setTickets([]); // Clear previous tickets
                try {
                    const fetchedData = await fetchTickets();
                    // Filter tickets related to this call if needed, or show all
                    setTickets(fetchedData || []);
                } catch (error) {
                    console.error("Error fetching tickets:", error);
                    setFetchTicketsError(error.message || "Could not load tickets.");
                } finally {
                    setIsLoadingTickets(false);
                }
            };
            loadTickets();
        }
    }, [isLogs, callId]);

    return (
        <div className="w-full h-full px-2 flex items-start justify-between flex-col">
            {isLogs ? (
                <div className={`w-full h-full ${tickets.length === 0 ? 'py-10' : ''}`}>
                    {isLoadingTickets && (
                        <div className="flex justify-center items-center py-10">
                            <Icon icon="eos-icons:loading" className="text-3xl text-primary" />
                            <p className="ml-2 text-gray-300">Loading tickets...</p>
                        </div>
                    )}
                    {fetchTicketsError && (
                        <div className="text-center py-10 text-error">
                            <Icon icon="material-symbols:error-outline" className="text-3xl mx-auto mb-2" />
                            <p>Error: {fetchTicketsError}</p>
                        </div>
                    )}
                    {!isLoadingTickets && !fetchTicketsError && tickets.length === 0 && (
                        <div className="flex flex-col items-center justify-center w-full text-center text-gray-400">
                            <Icon icon="material-symbols:confirmation-number-off-outline" className="text-5xl mb-2" />
                            <p>No tickets found.</p>
                        </div>
                    )}
                    {!isLoadingTickets && !fetchTicketsError && tickets.length > 0 && (
                        <div className="space-y-4">
                            {tickets.map((ticket) => (
                                <TicketCard key={ticket.id} ticket={ticket} />
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="w-full h-full">
                    <NewTicket />
                </div>
            )}
        </div>
    );
};